import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Helmet } from "react-helmet-async";
import { 
  Sparkles, User, Briefcase, ArrowRight, Loader2, 
  Rocket, Lightbulb, Zap, CheckCircle2, RefreshCw,
  QrCode, Copy, Mail, X, Maximize2, Brain, Gift,
  MessageCircle, Smartphone, Monitor, ArrowLeft, Home, Menu
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Images androïdes
const TRON_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/5c52dzth_1FF6B37C-6ED9-4724-AEBD-F1C8DDD70C73.png",
  guide: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/9ak2jpqm_1B94520E-3EBA-4CB1-9147-DE6B3C59B7DD.png",
  trainer: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/dr6znd2r_4641A994-51F7-4AEA-824E-5315348DDDF0.png",
  secondary: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/pnxjfnov_9EEA2BB1-33E3-4079-8844-3FDC343A1F3D.png",
};

// Détection des réponses farfelues
const SILLY_PATTERNS = [
  /^(test|asdf|qwerty|azerty|123|abc|xxx|zzz|aaa|bbb)/i,
  /^(je sais pas|aucune idée|n'importe quoi|blabla|toto|tata|titi)/i,
  /^(lol|mdr|haha|hihi|ptdr|xd|mouais)/i,
  /^.{0,3}$/,  // Réponse trop courte
  /^(.)\1{3,}/, // Caractères répétés
];

const FUNNY_RESPONSES = [
  "😄 Allez, un petit effort ! Qu'est-ce que vous aimeriez vraiment que l'IA fasse pour vous au travail ?",
  "🤖 Même une IA a besoin d'un peu plus d'infos ! Décrivez-nous votre souhait professionnel.",
  "😅 On dirait que vous testez le système ! Dites-nous plutôt ce que vous aimeriez automatiser.",
  "🎯 Soyons sérieux 2 secondes : quelle tâche répétitive aimeriez-vous confier à l'IA ?",
  "💡 L'IA ne lit pas encore dans les pensées ! Partagez-nous votre vraie demande.",
];

const JeuInteractifPage = () => {
  const { companyCode } = useParams();
  const [searchParams] = useSearchParams();
  const isMobileView = searchParams.get('mobile') === 'true';
  
  const [stage, setStage] = useState("intro");
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({
    prenom: "",
    fonction: "",
    email: "",
    demande_ia: ""
  });
  const [suggestions, setSuggestions] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showQRFullscreen, setShowQRFullscreen] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [sillyAttempts, setSillyAttempts] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});

  const currentUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}/jeu${companyCode ? `/${companyCode}` : ''}?mobile=true`
    : '';

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (companyCode) {
      fetchCompany(companyCode);
    }
  }, [companyCode]);

  const fetchCompany = async (code) => {
    try {
      const response = await axios.get(`${API}/companies/${code}`);
      setCompany(response.data);
      setStage("form");
    } catch (error) {
      console.error("Company not found:", error);
      // En mode démo, on continue quand même
      setStage("form");
    }
  };

  const validateField = (field, value) => {
    if (field === 'demande_ia') {
      const isSilly = SILLY_PATTERNS.some(pattern => pattern.test(value.trim()));
      if (isSilly) {
        setSillyAttempts(prev => prev + 1);
        return FUNNY_RESPONSES[Math.min(sillyAttempts, FUNNY_RESPONSES.length - 1)];
      }
      if (value.trim().length < 10) {
        return "Décrivez en au moins 10 caractères ce que vous souhaitez.";
      }
    }
    if (field === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "Veuillez entrer une adresse email valide.";
    }
    return null;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async () => {
    const errors = {};
    
    if (!formData.prenom.trim()) errors.prenom = "Votre prénom est requis";
    if (!formData.fonction.trim()) errors.fonction = "Votre fonction est requise";
    if (!formData.email.trim()) errors.email = "Votre email est requis";
    
    const emailError = validateField('email', formData.email);
    if (emailError) errors.email = emailError;
    
    const demandeError = validateField('demande_ia', formData.demande_ia);
    if (demandeError) errors.demande_ia = demandeError;
    
    if (!formData.demande_ia.trim()) errors.demande_ia = "Dites-nous ce que vous voulez que l'IA fasse pour vous !";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Toast avec humour si c'est une réponse farfelue
      if (errors.demande_ia && sillyAttempts > 0) {
        toast.error(errors.demande_ia, { duration: 5000 });
      }
      return;
    }

    setStage("generating");
    setIsGenerating(true);

    try {
      const response = await axios.post(`${API}/game/session-v2`, {
        company_code: companyCode || "DEMO",
        prenom: formData.prenom,
        fonction: formData.fonction,
        email: formData.email,
        demande_ia: formData.demande_ia
      });

      setSuggestions(response.data.suggested_apps);
      
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      setStage("results");
      toast.success("🎉 Vos applications IA sont prêtes !");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erreur lors de la génération. Réessayez !");
      setStage("form");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectApp = (app) => {
    setSelectedApp(app);
  };

  const handleReset = () => {
    setStage("form");
    setFormData({ prenom: "", fonction: "", email: "", demande_ia: "" });
    setSuggestions([]);
    setSelectedApp(null);
    setSillyAttempts(0);
    setFieldErrors({});
  };

  const complexityColors = {
    simple: "text-green-500 bg-green-500/10",
    moyen: "text-yellow-500 bg-yellow-500/10",
    avancé: "text-red-500 bg-red-500/10"
  };

  // QR Code Fullscreen Modal
  const QRFullscreenModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={() => setShowQRFullscreen(false)}
    >
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        className="text-center"
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={() => setShowQRFullscreen(false)}
          className="absolute top-4 right-4 text-white/60 hover:text-white p-2"
        >
          <X className="w-8 h-8" />
        </button>
        
        <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
          Scannez pour jouer ! 📱
        </h2>
        <p className="text-white/60 mb-8 text-lg">
          Créez votre première application IA en 30 secondes
        </p>
        
        <div className="bg-white p-6 md:p-10 rounded-3xl inline-block shadow-2xl shadow-[#007AFF]/20">
          <QRCodeSVG 
            value={currentUrl} 
            size={Math.min(400, typeof window !== 'undefined' ? window.innerWidth - 100 : 300)} 
            level="H"
          />
        </div>
        
        <p className="text-[#007AFF] mt-6 text-sm md:text-base">
          {currentUrl}
        </p>
      </motion.div>
    </motion.div>
  );

  // Version Mobile optimisée
  if (isMobileView) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a1a] to-[#050505]" data-testid="jeu-mobile">
        {/* Meta tags pour partage mobile */}
        <Helmet>
          <title>🎮 Créez votre App IA en 30 sec ! | Académie IA</title>
          <meta name="description" content="Découvrez ce que l'IA peut faire pour VOUS ! Créez gratuitement votre première application IA personnalisée en quelques secondes. Bonus : -20% offerts !" />
          
          {/* Open Graph / WhatsApp / iMessage */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://academie-ia.fr/jeu?mobile=true" />
          <meta property="og:title" content="🎮 Créez votre première App IA en 30 secondes !" />
          <meta property="og:description" content="Découvrez GRATUITEMENT ce que l'Intelligence Artificielle peut faire pour vous, à votre poste. Aucune connaissance technique requise ! Bonus : -20% offerts sur votre formation." />
          <meta property="og:image" content={TRON_IMAGES.guide} />
          <meta property="og:site_name" content="Académie IA - Jeu Interactif" />
          <meta property="og:locale" content="fr_FR" />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="🎮 Créez votre première App IA en 30 secondes !" />
          <meta name="twitter:description" content="Découvrez GRATUITEMENT ce que l'IA peut faire pour vous ! Bonus : -20% offerts sur votre formation." />
          <meta name="twitter:image" content={TRON_IMAGES.guide} />
        </Helmet>

        {/* Header Mobile avec navigation */}
        <div className="sticky top-0 z-40 bg-[#050505]/90 backdrop-blur-lg border-b border-white/10 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link 
                to="/" 
                className="w-10 h-10 rounded-xl bg-[#007AFF] flex items-center justify-center"
              >
                <Brain className="w-5 h-5 text-white" />
              </Link>
              <div>
                <p className="text-white font-bold text-sm">ACADÉMIE IA</p>
                <p className="text-[#007AFF] text-xs">Jeu Interactif</p>
              </div>
            </div>
            <Link 
              to="/"
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <Home className="w-5 h-5 text-white/60" />
            </Link>
          </div>
        </div>

        <div className="px-4 py-6">
          <AnimatePresence mode="wait">
            {/* Form Stage Mobile */}
            {(stage === "intro" || stage === "form") && (
              <motion.div
                key="form-mobile"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {/* Hero Image Mobile */}
                <div className="relative mb-6 rounded-2xl overflow-hidden">
                  <img 
                    src={TRON_IMAGES.guide}
                    alt="Lucie-IA"
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h1 className="text-2xl font-bold text-white">
                      Créez votre <span className="text-[#007AFF]">App IA</span> ! 🚀
                    </h1>
                  </div>
                </div>

                {/* Form Mobile */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/80 text-sm flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-[#007AFF]" />
                      Votre prénom
                    </Label>
                    <Input
                      value={formData.prenom}
                      onChange={(e) => handleInputChange('prenom', e.target.value)}
                      placeholder="Marie"
                      className={`h-12 text-base ${fieldErrors.prenom ? 'border-red-500' : ''}`}
                    />
                    {fieldErrors.prenom && <p className="text-red-400 text-xs mt-1">{fieldErrors.prenom}</p>}
                  </div>

                  <div>
                    <Label className="text-white/80 text-sm flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-[#007AFF]" />
                      Votre fonction
                    </Label>
                    <Input
                      value={formData.fonction}
                      onChange={(e) => handleInputChange('fonction', e.target.value)}
                      placeholder="Responsable Commercial"
                      className={`h-12 text-base ${fieldErrors.fonction ? 'border-red-500' : ''}`}
                    />
                    {fieldErrors.fonction && <p className="text-red-400 text-xs mt-1">{fieldErrors.fonction}</p>}
                  </div>

                  <div>
                    <Label className="text-white/80 text-sm flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4 text-[#007AFF]" />
                      Votre email
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="marie@entreprise.fr"
                      className={`h-12 text-base ${fieldErrors.email ? 'border-red-500' : ''}`}
                    />
                    {fieldErrors.email && <p className="text-red-400 text-xs mt-1">{fieldErrors.email}</p>}
                  </div>

                  <div>
                    <Label className="text-white/80 text-sm mb-2 block">
                      <span className="text-[#007AFF]">✨</span> Que voudriez-vous que l'IA fasse <strong>POUR VOUS</strong> demain ?
                    </Label>
                    <Textarea
                      value={formData.demande_ia}
                      onChange={(e) => handleInputChange('demande_ia', e.target.value)}
                      placeholder="Ex: Rédiger automatiquement mes comptes-rendus de réunion..."
                      rows={3}
                      className={`text-base ${fieldErrors.demande_ia ? 'border-red-500' : ''}`}
                    />
                    {fieldErrors.demande_ia && (
                      <p className="text-amber-400 text-xs mt-1">{fieldErrors.demande_ia}</p>
                    )}
                  </div>

                  <button
                    onClick={handleSubmit}
                    className="btn-primary w-full h-14 text-lg flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    Créer mon App IA !
                  </button>
                </div>
              </motion.div>
            )}

            {/* Generating Stage Mobile */}
            {stage === "generating" && (
              <motion.div
                key="generating-mobile"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-16"
              >
                <div className="w-24 h-24 rounded-full bg-[#007AFF]/20 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-12 h-12 text-[#007AFF] animate-spin" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">
                  L'IA imagine vos apps...
                </h2>
                <p className="text-white/60">
                  {formData.prenom}, patientez quelques secondes ✨
                </p>
              </motion.div>
            )}

            {/* Results Stage Mobile */}
            {stage === "results" && (
              <motion.div
                key="results-mobile"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    {formData.prenom}, voici vos Apps IA ! 🎉
                  </h2>
                </div>

                <div className="space-y-3 mb-6">
                  {suggestions.map((app, index) => (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleSelectApp(app)}
                      className={`w-full glass-card p-4 text-left transition-all ${
                        selectedApp === app ? 'border-[#007AFF] ring-2 ring-[#007AFF]/20' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                          {index === 0 && <Lightbulb className="w-5 h-5 text-[#007AFF]" />}
                          {index === 1 && <Zap className="w-5 h-5 text-[#007AFF]" />}
                          {index === 2 && <Rocket className="w-5 h-5 text-[#007AFF]" />}
                          {index === 3 && <Sparkles className="w-5 h-5 text-[#007AFF]" />}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-sm">{app.name}</h3>
                          <p className="text-white/60 text-xs mt-1">{app.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[#007AFF] text-xs">{app.benefit}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${complexityColors[app.complexity]}`}>
                              {app.complexity}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {/* Promo Card */}
                <div className="glass-card p-4 border-green-500/30 bg-green-500/5 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Gift className="w-6 h-6 text-green-500" />
                    <p className="text-green-400 font-semibold text-sm">-20% sur votre formation !</p>
                  </div>
                  <p className="text-white/70 text-xs">
                    Un email avec votre code promo vous a été envoyé à {formData.email}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleReset}
                    className="btn-secondary w-full flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Recommencer
                  </button>
                  
                  <Link
                    to="/tunnel"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    Demander ma formation
                  </Link>
                  
                  <Link
                    to="/"
                    className="w-full flex items-center justify-center gap-2 py-3 text-white/60 hover:text-white transition-colors"
                  >
                    <Home className="w-4 h-4" />
                    Retour à l'accueil
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // Version Desktop
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="jeu-interactif-page">
      <Navigation />
      
      {/* QR Fullscreen Modal */}
      <AnimatePresence>
        {showQRFullscreen && <QRFullscreenModal />}
      </AnimatePresence>

      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <AnimatePresence mode="wait">
            {/* Intro Stage Desktop */}
            {stage === "intro" && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {/* Hero Section */}
                <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
                  <div>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 mb-6"
                    >
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-green-400 font-semibold text-sm">100% GRATUIT</span>
                    </motion.div>
                    
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-4">
                      Développez votre <span className="text-[#007AFF]">première App IA</span><br />
                      en 30 secondes ! 🚀
                    </h1>
                    
                    <p className="text-lg text-white/60 mb-8 max-w-lg">
                      Découvrez concrètement ce que l'Intelligence Artificielle peut faire <strong className="text-white">pour vous, à votre poste</strong>. 
                      Aucune connaissance technique requise !
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <button
                        onClick={() => setStage("form")}
                        className="btn-primary text-lg px-8 py-4 flex items-center justify-center gap-2"
                      >
                        <Sparkles className="w-5 h-5" />
                        Commencer maintenant
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Hero Image */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                    className="relative"
                  >
                    <img 
                      src={TRON_IMAGES.trainer}
                      alt="Académie IA - Jeu Interactif"
                      className="w-full h-[400px] md:h-[500px] object-cover rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent rounded-3xl" />
                  </motion.div>
                </div>

                {/* QR Code Section for Trainers */}
                <div className="glass-card p-8 border-[#007AFF]/20">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#007AFF] flex items-center justify-center">
                          <QrCode className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">Mode Formateur</h3>
                          <p className="text-white/50 text-sm">Pour vos sessions en salle</p>
                        </div>
                      </div>
                      
                      <p className="text-white/70 mb-6">
                        Affichez ce QR Code en grand sur votre vidéoprojecteur. 
                        Vos participants peuvent scanner et jouer depuis leur smartphone !
                      </p>

                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowQRFullscreen(true)}
                          className="btn-primary flex items-center gap-2"
                        >
                          <Maximize2 className="w-4 h-4" />
                          Afficher en GRAND
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(currentUrl);
                            toast.success("Lien copié !");
                          }}
                          className="btn-secondary flex items-center gap-2"
                        >
                          <Copy className="w-4 h-4" />
                          Copier le lien
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-center">
                      <button
                        onClick={() => setShowQRFullscreen(true)}
                        className="bg-white p-6 rounded-2xl hover:shadow-lg hover:shadow-[#007AFF]/20 transition-all cursor-pointer"
                      >
                        <QRCodeSVG value={currentUrl} size={200} level="H" />
                        <p className="text-gray-500 text-xs mt-3 text-center">Cliquez pour agrandir</p>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Form Stage Desktop */}
            {stage === "form" && (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="grid md:grid-cols-2 gap-8 items-start">
                  {/* Form */}
                  <div className="glass-card p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-[#007AFF] flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Créez votre App IA</h2>
                        <p className="text-white/50 text-sm">3 questions, 30 secondes</p>
                      </div>
                    </div>

                    <div className="space-y-5">
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <User className="w-4 h-4 text-[#007AFF]" />
                          Votre prénom
                        </Label>
                        <Input
                          value={formData.prenom}
                          onChange={(e) => handleInputChange('prenom', e.target.value)}
                          placeholder="Marie"
                          className={fieldErrors.prenom ? 'border-red-500' : ''}
                        />
                        {fieldErrors.prenom && <p className="text-red-400 text-xs mt-1">{fieldErrors.prenom}</p>}
                      </div>

                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-4 h-4 text-[#007AFF]" />
                          Votre fonction
                        </Label>
                        <Input
                          value={formData.fonction}
                          onChange={(e) => handleInputChange('fonction', e.target.value)}
                          placeholder="Responsable Commercial, DAF, DRH..."
                          className={fieldErrors.fonction ? 'border-red-500' : ''}
                        />
                        {fieldErrors.fonction && <p className="text-red-400 text-xs mt-1">{fieldErrors.fonction}</p>}
                      </div>

                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <Mail className="w-4 h-4 text-[#007AFF]" />
                          Votre email professionnel
                        </Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="marie@entreprise.fr"
                          className={fieldErrors.email ? 'border-red-500' : ''}
                        />
                        {fieldErrors.email && <p className="text-red-400 text-xs mt-1">{fieldErrors.email}</p>}
                      </div>

                      <div>
                        <Label className="mb-2 block">
                          <span className="text-[#007AFF]">✨</span> Que voudriez-vous que l'IA fasse <strong className="text-[#007AFF]">POUR VOUS, À VOTRE POSTE</strong>, demain ?
                        </Label>
                        <Textarea
                          value={formData.demande_ia}
                          onChange={(e) => handleInputChange('demande_ia', e.target.value)}
                          placeholder="Ex: Rédiger automatiquement mes comptes-rendus de réunion, analyser mes tableaux Excel, répondre aux emails clients..."
                          rows={4}
                          className={fieldErrors.demande_ia ? 'border-amber-500' : ''}
                        />
                        {fieldErrors.demande_ia && (
                          <p className="text-amber-400 text-sm mt-2 p-2 bg-amber-500/10 rounded-lg">
                            {fieldErrors.demande_ia}
                          </p>
                        )}
                      </div>

                      <button
                        onClick={handleSubmit}
                        className="btn-primary w-full text-lg py-4 flex items-center justify-center gap-2"
                      >
                        <Sparkles className="w-5 h-5" />
                        Générer mes applications IA
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Side Image + Info */}
                  <div className="space-y-6">
                    <div className="relative rounded-2xl overflow-hidden">
                      <img 
                        src={TRON_IMAGES.guide}
                        alt="Lucie-IA vous guide"
                        className="w-full h-[300px] object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <p className="text-white font-semibold">Lucie-IA</p>
                        <p className="text-white/60 text-sm">Votre assistante Académie IA</p>
                      </div>
                    </div>

                    <div className="glass-card p-6 border-amber-500/20 bg-amber-500/5">
                      <p className="text-amber-400 font-semibold mb-2">💡 Le saviez-vous ?</p>
                      <p className="text-white/70 text-sm">
                        78% des salariés utilisent l'IA sans formation. 
                        Ils n'exploitent que 5% du potentiel de ces outils !
                      </p>
                    </div>

                    <div className="glass-card p-6 border-green-500/20 bg-green-500/5">
                      <div className="flex items-center gap-3 mb-2">
                        <Gift className="w-5 h-5 text-green-500" />
                        <p className="text-green-400 font-semibold">Bonus : -20% offerts !</p>
                      </div>
                      <p className="text-white/70 text-sm">
                        En jouant, recevez un code promo exclusif pour votre formation personnalisée.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Generating Stage Desktop */}
            {stage === "generating" && (
              <motion.div
                key="generating"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-20"
              >
                <div className="max-w-md mx-auto">
                  <div className="relative mb-8">
                    <img 
                      src={TRON_IMAGES.secondary}
                      alt="Génération en cours"
                      className="w-48 h-48 rounded-full mx-auto object-cover border-4 border-[#007AFF]/30"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-52 h-52 rounded-full border-4 border-[#007AFF] border-t-transparent animate-spin" />
                    </div>
                  </div>
                  
                  <h2 className="text-2xl font-bold text-white mb-2">
                    L'IA imagine vos applications...
                  </h2>
                  <p className="text-white/60 mb-4">
                    {formData.prenom}, nous analysons votre demande pour créer des solutions sur mesure.
                  </p>
                  
                  <div className="flex justify-center gap-2">
                    {[0, 1, 2].map(i => (
                      <motion.div
                        key={i}
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.2 }}
                        className="w-3 h-3 rounded-full bg-[#007AFF]"
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Results Stage Desktop */}
            {stage === "results" && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="text-center mb-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4"
                  >
                    <CheckCircle2 className="w-10 h-10 text-green-500" />
                  </motion.div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                    🎉 {formData.prenom}, vos apps IA sont prêtes !
                  </h1>
                  <p className="text-white/60">
                    Basées sur votre demande : "{formData.demande_ia.slice(0, 50)}..."
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {suggestions.map((app, index) => (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleSelectApp(app)}
                      className={`glass-card p-6 text-left transition-all ${
                        selectedApp === app 
                          ? 'border-[#007AFF] ring-2 ring-[#007AFF]/20' 
                          : 'hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                          {index === 0 && <Lightbulb className="w-7 h-7 text-[#007AFF]" />}
                          {index === 1 && <Zap className="w-7 h-7 text-[#007AFF]" />}
                          {index === 2 && <Rocket className="w-7 h-7 text-[#007AFF]" />}
                          {index === 3 && <Sparkles className="w-7 h-7 text-[#007AFF]" />}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{app.name}</h3>
                          <p className="text-white/60 text-sm mb-3">{app.description}</p>
                          <div className="flex items-center gap-3">
                            <span className="text-[#007AFF] text-sm font-medium">{app.benefit}</span>
                            <span className={`px-2 py-1 rounded-full text-xs ${complexityColors[app.complexity]}`}>
                              {app.complexity}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {/* Promo Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="glass-card p-6 border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/10 mb-8"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <Gift className="w-8 h-8 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-green-400 mb-1">
                        🎁 -20% sur votre formation personnalisée !
                      </h3>
                      <p className="text-white/70">
                        Un email avec votre code promo exclusif a été envoyé à <strong className="text-white">{formData.email}</strong>
                      </p>
                    </div>
                  </div>
                </motion.div>

                <div className="flex flex-wrap gap-4 justify-center">
                  <button
                    onClick={handleReset}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Recommencer
                  </button>
                  <a
                    href="/tunnel"
                    className="btn-primary flex items-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    Demander ma formation
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JeuInteractifPage;
